import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { issueTypePrioritySortFn } from "@overmap-ai/common"
import { useSort, useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement } from "react"

import { issuePriorityFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import {
	IssuePrioritySelectGroup,
	type IssuePrioritySelectGroupProps,
} from "../MenuSelectGroups/IssuePrioritySelectGroup"

type IssuePrioritySelectProps = Omit<MenuRootProps, "children"> &
	Omit<MenuClickTriggerProps, "children"> &
	MenuVariantProps &
	IssuePrioritySelectGroupProps & {
		children: ReactElement
	}

export const IssuePrioritySelect = memo<IssuePrioritySelectProps>((props) => {
	const {
		// Trigger props
		children,
		disabled = false,
		// MenuContent props
		size,
		radius,
		variant,
		accentColor,
		// Priority props
		priorities,
		required,
		defaultValue,
		value,
		onValueChange,
		// Rest props
		...rest
	} = props

	const sortedPriorities = useSort(priorities, issueTypePrioritySortFn)

	const [filteredPriorities, filterValue, setFilterValue] = useTextFilter(
		sortedPriorities,
		issuePriorityFilterCallback,
	)

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{filteredPriorities.length > 0 && <Menu.Separator />}
				<Menu.Scroll>
					{required ? (
						<IssuePrioritySelectGroup
							priorities={filteredPriorities}
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
						/>
					) : (
						<IssuePrioritySelectGroup
							priorities={filteredPriorities}
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
						/>
					)}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
IssuePrioritySelect.displayName = "IssuePrioritySelect"
